export class LocalStorageService {
	constructor(prefix = '') {
		this.prefix = prefix;
		this.dbName = 'a11yfix_storage_v1';
		this.storeName = 'kv';
		this.cache = new Map();
		this.dbPromise = this.openDb();
	}

	openDb() {
		if (typeof window === 'undefined' || !window.indexedDB) {
			return Promise.resolve(null);
		}

		return new Promise((resolve) => {
			let request;

			try {
				request = window.indexedDB.open(this.dbName, 1);
			} catch {
				resolve(null);
				return;
			}

			request.onupgradeneeded = () => {
				const db = request.result;

				if (!db.objectStoreNames.contains(this.storeName)) {
					db.createObjectStore(this.storeName, { keyPath: 'key' });
				}
			};

			request.onsuccess = () => {
				resolve(request.result || null);
			};

			request.onerror = () => {
				resolve(null);
			};

			request.onblocked = () => {
				resolve(null);
			};
		});
	}

	async withStore(mode, callback) {
		const db = await this.dbPromise;

		if (!db) {
			return null;
		}

		return new Promise((resolve) => {
			let transaction;

			try {
				transaction = db.transaction(this.storeName, mode);
			} catch {
				resolve(null);
				return;
			}

			const store = transaction.objectStore(this.storeName);
			const request = callback(store);

			if (!request) {
				resolve(null);
				return;
			}

			request.onsuccess = () => {
				resolve(request.result ?? null);
			};

			request.onerror = () => {
				resolve(null);
			};
		});
	}

	async hydrateKeys(keys = []) {
		if (!Array.isArray(keys) || keys.length === 0) {
			return;
		}

		const uniqueKeys = Array.from(
			new Set(
				keys
					.map((key) => this.buildKey(key))
					.filter((key) => typeof key === 'string' && key.length > 0)
			)
		);

		await Promise.all(
			uniqueKeys.map(async (storageKey) => {
				const result = await this.withStore('readonly', (store) => store.get(storageKey));

				if (result && typeof result.value === 'string') {
					this.cache.set(storageKey, result.value);
				}
			})
		);
	}

	buildKey(key) {
		if (!this.prefix) {
			return key;
		}

		return `${this.prefix}${key}`;
	}

	getJson(key, fallback = null) {
		try {
			const raw = this.cache.get(this.buildKey(key));

			if (!raw) {
				return fallback;
			}

			const parsed = JSON.parse(raw);
		
			return parsed ?? fallback;
		} catch {
			return fallback;
		}
	}

	async getJsonAsync(key, fallback = null) {
		const storageKey = this.buildKey(key);

		if (this.cache.has(storageKey)) {
			return this.getJson(key, fallback);
		}

		const result = await this.withStore('readonly', (store) => store.get(storageKey));
		
		if (result && typeof result.value === 'string') {
			this.cache.set(storageKey, result.value);
			return this.getJson(key, fallback);
		}

		return fallback;
	}

	setJson(key, value) {
		try {
			const storageKey = this.buildKey(key);
			const serialized = JSON.stringify(value);
			this.cache.set(storageKey, serialized);

			void this.withStore('readwrite', (store) => {
				return store.put({
					key: storageKey,
					value: serialized,
					updatedAt: Date.now()
				});
			});

			return true;
		} catch {
			return false;
		}
	}

	remove(key) {
		try {
			const storageKey = this.buildKey(key);
			this.cache.delete(storageKey);

			void this.withStore('readwrite', (store) => store.delete(storageKey));

			return true;
		} catch {
			return false;
		}
	}
}
